import { useEffect, useRef, useState } from "react";
import { CheckCircle2, CreditCard, Loader2, Pencil, RefreshCw, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { expiryIso, scanIdDocument } from "@/lib/noleggio";
import { AlertTriangle } from "lucide-react";
import PhotoButtons from "@/components/PhotoButtons";

export const EMPTY_CUSTOMER = {
  full_name: "", tax_code: "", birth_date: "", address: "", city_zip: "", nationality: "Italiana",
  doc_type: "", doc_number: "", doc_issuer_expiry: "", emergency_contact: "", doc_front_id: "", doc_back_id: "",
};

export function toIsoDate(v) {
  const m = v.trim().match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (!m) return "";
  const [d, mo, y] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCDate() !== d || dt.getUTCMonth() !== mo - 1 || y < 1900 || dt > new Date()) return "";
  return `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
export function fromIsoDate(v) {
  const m = (v || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : v || "";
}

export function customerErrors(c) {
  const bad = [];
  if (c.full_name.trim().length < 3) bad.push("full_name");
  if (!/^[A-Za-z0-9]{11,20}$/.test(c.tax_code.trim())) bad.push("tax_code");
  if (!toIsoDate(c.birth_date)) bad.push("birth_date");
  if (c.address.trim().length < 3) bad.push("address");
  if (c.city_zip.trim().length < 3) bad.push("city_zip");
  if (c.nationality.trim().length < 2) bad.push("nationality");
  if (!c.doc_type) bad.push("doc_type");
  if (c.doc_number.trim().length < 4) bad.push("doc_number");
  if (c.doc_issuer_expiry.trim().length < 3) bad.push("doc_issuer_expiry");
  if (!c.doc_front_id) bad.push("doc_front_id");
  if (!c.doc_back_id) bad.push("doc_back_id");
  return bad;
}
const dataErrors = (c) => customerErrors(c).filter((k) => k !== "doc_front_id" && k !== "doc_back_id");

const FIELDS = [
  { key: "full_name" }, { key: "tax_code", ph: "RSSMRA80A01L191X" }, { key: "birth_date", phKey: "birth_ph" },
  { key: "address" }, { key: "city_zip", phKey: "city_ph" }, { key: "nationality" },
];
const DOC_FIELDS = [{ key: "doc_number" }, { key: "doc_issuer_expiry", phKey: "issuer_ph" }, { key: "emergency_contact" }];

export const docExpiredFor = (c, lastDay) => {
  const e = expiryIso(c.doc_issuer_expiry);
  return e && lastDay && e < lastDay ? e : "";
};

export function CustomerForm({ value, onChange, showErrors, lastDay }) {
  const { t } = useI18n();
  const [files, setFiles] = useState({});
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [open, setOpen] = useState(false);
  const latest = useRef(value);
  latest.current = value;

  const missing = dataErrors(value);
  const bad = showErrors ? customerErrors(value) : [];
  const showFields = open || (missing.length > 0 && (scanned || showErrors));
  const set = (k, v) => onChange({ ...latest.current, [k]: k === "tax_code" ? v.toUpperCase() : v });

  const scan = async (f) => {
    setScanning(true);
    try {
      const r = await scanIdDocument(f.front, f.back);
      const filled = Object.fromEntries(Object.entries(r).filter(([k, v]) => k !== "readable" && k !== "doc_expiry" && v));
      onChange({ ...latest.current, ...filled });
      toast.success(t("scan_done"));
    } catch (e) {
      toast.error(e.message);
      setOpen(true);
    } finally {
      setScanned(true);
      setScanning(false);
    }
  };

  useEffect(() => {
    if (files.front && files.back) scan(files);
  }, [files]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPhoto = (side) => (id, file) => {
    onChange({ ...latest.current, [side === "front" ? "doc_front_id" : "doc_back_id"]: id });
    setFiles((p) => ({ ...p, [side]: file }));
  };

  const input = (f) => (
    <label key={f.key} className="flex flex-col gap-1">
      <span className="mn-label">{t(`cust_${f.key}`)}</span>
      <input data-testid={`customer-${f.key}`} className={`mn-input ${bad.includes(f.key) || (scanned && missing.includes(f.key)) ? "mn-input-bad" : ""}`}
        value={value[f.key]} placeholder={f.phKey ? t(f.phKey) : f.ph} onChange={(e) => set(f.key, e.target.value)} />
    </label>
  );

  return (
    <div data-testid="customer-form" className="mn-card p-5 flex flex-col gap-3">
      <p className="text-sm text-[var(--text-2)] flex gap-2"><ScanLine size={18} className="shrink-0 text-[var(--brand-primary)]" />{t("scan_hint")}</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <DocPhoto side="front" id={value.doc_front_id} bad={bad.includes("doc_front_id")} onUploaded={onPhoto("front")} />
        <DocPhoto side="back" id={value.doc_back_id} bad={bad.includes("doc_back_id")} onUploaded={onPhoto("back")} />
      </div>

      {scanning ? (
        <div data-testid="customer-scanning" className="rounded-xl bg-[var(--brand-tertiary)] p-4 flex items-center gap-3 text-sm font-semibold text-[var(--brand-primary)]">
          <Loader2 size={18} className="animate-spin" /> {t("scanning")}
        </div>
      ) : !showFields && missing.length === 0 ? (
        <div data-testid="customer-summary" className="rounded-xl bg-[var(--brand-tertiary)] p-4 flex items-center gap-3">
          <CheckCircle2 className="shrink-0" color="var(--brand-primary)" />
          <div className="flex-1 min-w-0 text-sm">
            <p data-testid="customer-summary-name" className="font-bold truncate">{value.full_name}</p>
            <p className="text-[var(--text-2)] truncate">{value.tax_code} · {t("born", { d: value.birth_date })}</p>
            <p className="text-[var(--text-2)] truncate">{t(`doc_${value.doc_type}`)} {value.doc_number}</p>
          </div>
          <button type="button" data-testid="customer-edit-button" onClick={() => setOpen(true)} className="mn-btn mn-btn-ghost !min-h-[40px] !px-4 text-sm bg-white"><Pencil size={15} /> {t("edit")}</button>
        </div>
      ) : !showFields ? (
        <button type="button" data-testid="customer-manual-button" onClick={() => setOpen(true)} className="text-sm font-semibold text-[var(--brand-primary)] self-start py-1">{t("fill_manually")}</button>
      ) : (
        <div data-testid="customer-fields" className="flex flex-col gap-3 mn-rise">
          {scanned && missing.length > 0 && <p data-testid="customer-scan-missing" className="text-sm font-semibold text-[#c97a00]">{t("scan_missing")}</p>}
          <div className="grid sm:grid-cols-2 gap-3">{FIELDS.map(input)}</div>
          <span className="mn-label mt-1">{t("cust_doc_type")}</span>
          <div className={`flex flex-wrap gap-2 rounded-xl border p-0.5 ${bad.includes("doc_type") ? "border-[var(--error)]" : "border-transparent"}`}>
            {["ci", "patente", "passaporto"].map((k) => (
              <button key={k} type="button" data-testid={`customer-doc-${k}`} onClick={() => set("doc_type", k)} className={`mn-chip ${value.doc_type === k ? "mn-chip-on" : ""}`}>{t(`doc_${k}`)}</button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">{DOC_FIELDS.map(input)}</div>
          <div className="flex gap-2 flex-wrap">
            {files.front && files.back && (
              <button type="button" data-testid="customer-rescan-button" onClick={() => scan(files)} className="mn-btn mn-btn-ghost !min-h-[40px] text-sm"><RefreshCw size={15} /> {t("rescan")}</button>
            )}
            {missing.length === 0 && <button type="button" data-testid="customer-close-fields" onClick={() => setOpen(false)} className="mn-btn mn-btn-ghost !min-h-[40px] text-sm">{t("hide_fields")}</button>}
          </div>
        </div>
      )}
      {docExpiredFor(value, lastDay) && (
        <div data-testid="customer-doc-expired" className="rounded-xl bg-red-50 border border-red-200 p-4 flex gap-3 text-sm font-semibold text-[var(--error)]">
          <AlertTriangle size={18} className="shrink-0" />
          {t("doc_expired", { d: docExpiredFor(value, lastDay).split("-").reverse().join("/") })}
        </div>
      )}
      {bad.length > 0 && <p data-testid="customer-form-error" className="text-sm font-semibold text-[var(--error)]">{t("cust_invalid")}</p>}
    </div>
  );
}

function DocPhoto({ side, id, bad, onUploaded }) {
  const { t } = useI18n();
  const [preview, setPreview] = useState(null);
  const prefix = `customer-doc-${side}`;
  return (
    <div data-testid={`${prefix}-box`} className={`rounded-xl border p-3 flex flex-col gap-2 ${bad ? "border-[var(--error)]" : "border-[var(--border)]"}`}>
      <div className="flex items-center gap-3">
        {preview ? <img src={preview} alt="" className="w-[72px] h-[46px] rounded-lg object-cover" /> : (
          <div className="w-[72px] h-[46px] rounded-lg bg-[var(--surface-2)] flex items-center justify-center">
            {id ? <CheckCircle2 color="var(--brand-primary)" /> : <CreditCard color="var(--muted)" />}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="mn-label">{t(side === "front" ? "doc_front" : "doc_back")}</p>
          <p data-testid={`${prefix}-status`} className="text-xs text-[var(--text-2)]">{id ? t("doc_photo_ok") : t("doc_photo_missing")}</p>
        </div>
      </div>
      <PhotoButtons path="/me/id-document" prefix={prefix} successKey="doc_uploaded" onUploaded={(d, uri, file) => { setPreview(uri); onUploaded(d.id, file); }} />
    </div>
  );
}
